import { createApi } from '@reduxjs/toolkit/query/react';
import { graphqlRequestBaseQuery } from '@rtk-query/graphql-request-base-query';

import {
  REACT_APP_AUTH_INFO,
  REACT_APP_SERVICE_ID,
  REACT_APP_SERVICE_KEY,
  REACT_APP_CUCON_API_DOMAIN,
} from '@/config';
import { authenticationMethodType, CUCON_API_PATH } from '@/constants';
import { checkAndRefreshToken } from '@/features/authenticator/api/oidcAuth';
import { fetchAuthorizationTenantServiceInfo } from '@/features/authorizationInfo/api/fetchAuthorizationInfo';
import { amplifySignOut, sessionLoader } from '@/lib/amplify';
// NOTE: jsonのプロファイルを利用する。本ファイルのcreateApiメソッドは、storeの初期化時に利用しており、CUCONから取得したserviceProfileを利用できないため
import { serviceProfile as jsonServiceProfile } from '@/profile/serviceProfile';
import {
  getToken,
  clearToken,
  setToken,
  CUCON_API_KEY_NAME,
} from '@/utils/storage';

export type AuthorizationInfoEntity = {
  tenantAlias?: string;
  userPoolId?: string;
  clientIds?: string;
  clientId?: string;
  apiKey?: string;
};

export type AuthorizationInfoResponse = {
  data?: {
    authorizationTenantServiceInfo: Array<AuthorizationInfoEntity>;
  };
  errors?: Array<unknown>;
};

let isSignIn = false;
let isOidcSignIn = false;
let oidcToken = '';
let oidcRefreshToken = '';
let tenantId = '';

const setSessionCookie = (name: string, value: string) => {
  if (typeof document === 'undefined') {
    return;
  }

  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax`;
};

export const setSignInState = (signIn: boolean) => {
  isSignIn = signIn;
};

export const setOidcSignInState = (
  signIn: boolean,
  token: string,
  refreshToken: string,
  id: string
) => {
  isOidcSignIn = signIn;
  oidcToken = token;
  tenantId = id;
  oidcRefreshToken = refreshToken;
};

export const getOidcSignInState = () => {
  return {
    isOidcSignIn,
    oidcToken,
    oidcRefreshToken,
  };
};

export const api = createApi({
  baseQuery: async (args, api, extraOptions) => {
    const { UserByUsernameDocument, TenantPublicInfoDocument } = await import(
      '@/app/api/generated'
    );

    let apiUrl =
      jsonServiceProfile?.cuconConfig?.url ??
      REACT_APP_CUCON_API_DOMAIN + CUCON_API_PATH.hasura;

    if (
      args.document === UserByUsernameDocument ||
      args.document === TenantPublicInfoDocument
    ) {
      apiUrl =
        jsonServiceProfile?.cuconConfig?.appSyncUrl ??
        REACT_APP_CUCON_API_DOMAIN + CUCON_API_PATH.appSync;
    }

    const baseQuery = graphqlRequestBaseQuery({
      url: apiUrl,
      prepareHeaders: async (headers) => {
        // 本番環境のapiKeyの移行は随時対応するため一旦こちらの実装で行う
        if (isSignIn) {
          const authInfo = await sessionLoader();
          if (authInfo !== undefined) {
            headers.set('Authorization', `Bearer ${authInfo?.idToken}`);
            headers.set(
              'Service-Key',
              jsonServiceProfile?.cuconConfig?.serviceKey ??
                REACT_APP_SERVICE_KEY
            );
          } else {
            // トークンの更新に失敗したらサインアウトする
            await amplifySignOut();
            isSignIn = false;
            const apiKey =
              jsonServiceProfile?.cuconConfig?.apiKey ??
              getToken(CUCON_API_KEY_NAME);

            if (apiKey != null) {
              headers.set('api-key', apiKey);
            }
          }
        } else if (isOidcSignIn) {
          const auth = await checkAndRefreshToken(oidcToken, oidcRefreshToken);
          if (
            auth !== null &&
            auth.accessToken !== null &&
            auth.refreshToken !== null
          ) {
            oidcToken = auth.accessToken;
            oidcRefreshToken = auth.refreshToken;
            setSessionCookie('accessToken', auth.accessToken);
            setSessionCookie('refreshToken', auth.refreshToken);
            headers.set('Authorization', `Bearer ${oidcToken}`);
            headers.set('cucon-tenant', tenantId);
            headers.set(
              'Service-Key',
              jsonServiceProfile?.cuconConfig?.serviceKey ??
                REACT_APP_SERVICE_KEY
            );
          } else {
            isOidcSignIn = false;
            oidcToken = '';
            oidcRefreshToken = '';

            const apiKey =
              jsonServiceProfile?.cuconConfig?.apiKey ??
              getToken(CUCON_API_KEY_NAME);

            if (apiKey != null) {
              headers.set('api-key', apiKey);
            }
          }
        } else {
          const apiKey =
            jsonServiceProfile?.cuconConfig?.apiKey ??
            getToken(CUCON_API_KEY_NAME);

          if (apiKey != null) {
            headers.set('api-key', apiKey);
          }
        }

        return headers;
      },
      customErrors: async ({ name, stack, response }) => {
        if (jsonServiceProfile?.cuconConfig?.apiKey == null) {
          try {
            const authorizationTenantServiceInfoApiUrl =
              jsonServiceProfile?.cuconConfig
                ?.authorizationTenantServiceInfoUrl ??
              REACT_APP_CUCON_API_DOMAIN +
                CUCON_API_PATH.authorizationTenantServiceInfo;
            const authInfo =
              jsonServiceProfile?.cuconConfig?.authInfo ?? REACT_APP_AUTH_INFO;
            const variables = {
              tenantAlias: jsonServiceProfile?.cuconConfig?.tenantAlias,
              serviceId:
                jsonServiceProfile?.cuconConfig?.serviceId ??
                REACT_APP_SERVICE_ID,
            };
            const res = await fetchAuthorizationTenantServiceInfo(
              variables,
              authorizationTenantServiceInfoApiUrl,
              authInfo
            );
            const apiKey =
              res.data?.authorizationTenantServiceInfo[0].apiKey ?? '';
            setToken(CUCON_API_KEY_NAME, apiKey);
          } catch (error) {
            console.error('getAuthorizationInfo: ', error);
            clearToken(CUCON_API_KEY_NAME);
          }
        }
        if (
          jsonServiceProfile?.cuconConfig?.authenticationMethod ===
            authenticationMethodType.API_KEY_AND_TOKEN ||
          jsonServiceProfile?.cuconConfig?.authenticationMethod ===
            authenticationMethodType.TOKEN ||
          jsonServiceProfile?.cuconConfig?.authenticationMethod ===
            authenticationMethodType.ADMIN_TOKEN
        ) {
          // 認証エラーの場合、再度認証情報をチェックする
          if (response.status === 401) {
            const ret = await sessionLoader();

            if (ret !== undefined) {
              // セッションが維持できた場合はエラーにしない
              return {
                name: '',
                message: response.message,
                errorCode: response.statusCode,
                stack,
              };
            }
          }
        }

        //下記returnがrtk queryのエラーレスポンスとして返ってくる
        return {
          name,
          message: response.message,
          errorCode: response.statusCode,
          stack,
        };
      },
    });

    return baseQuery(args, api, extraOptions);
  },
  endpoints: () => ({}),
});
